// Phase 9.5 — Per-location detail view (drill-down from the tile grid).
//
// Header: breadcrumb, location name + health pill + address, stat row.
// Body: sortable items table with sales-driven columns (qty_sold_7d/14d/30d/90d),
//       reorder / danger levels (sales-calculated with sheet fallback),
//       and per-row status pill.
// Toggle: "Show inactive items" — includes is_menu_active=false rows
//         (rendered greyed out) but still excluded from the header stats.
// CSV export: uses the same query params.
//
// Deep-link params: ?status=out_of_stock | ?category=Grocery | ?search=

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError, API } from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { STATUS_META } from "./statusMeta";

const HEALTH_PILL = {
  green:  { cls: "bg-emerald-500/10 text-emerald-800 border-emerald-500/40", label: "Green" },
  yellow: { cls: "bg-amber-500/10   text-amber-800   border-amber-500/40",   label: "Yellow" },
  red:    { cls: "bg-rose-500/10    text-rose-800    border-rose-500/40",    label: "Red" },
};

function num(v, decimals = 2) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: decimals });
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status, cls: "bg-slate-200 text-slate-700" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${meta.cls}`}
      data-testid={`status-pill-${status}`}
    >
      {meta.label}
    </span>
  );
}

export default function FnbPoLocationDetail() {
  const { location_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Phase 9.6.1 addendum — per-row applicability toggles.
  // `applicabilityView` = "all" | "applicable" | "not_applicable" | "auto_excluded"
  const [applicabilityView, setApplicabilityView] = useState("applicable");
  const [selectedSkus, setSelectedSkus] = useState(() => new Set());
  const [savingSku, setSavingSku] = useState(null);
  const [savingBulk, setSavingBulk] = useState(false);

  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [includeInactiveHere, setIncludeInactiveHere] = useState(() => {
    // Phase 9.5.1 — persist the "show items inactive at this outlet" toggle.
    try {
      return localStorage.getItem("fnb.locationDetail.includeInactiveHere") === "1";
    } catch (_e) {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        "fnb.locationDetail.includeInactiveHere",
        includeInactiveHere ? "1" : "0",
      );
    } catch (_e) { /* ignore */ }
  }, [includeInactiveHere]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Phase 9.6.1 addendum — Location detail now uses the dedicated
      // `/locations/{id}/items` endpoint so the frontend receives EVERY
      // item at this location with its `is_applicable` + source, and
      // the operator can toggle them from the table.
      const params = new URLSearchParams();
      params.set("item_scope", "daily");
      params.set("filter", applicabilityView);
      if (debouncedSearch) params.set("search", debouncedSearch);
      const res = await api.get(
        `/fnb/locations/${encodeURIComponent(location_id)}/items?${params}`,
      );
      setDetail(res.data);
    } catch (e) {
      const msg = detailFromError(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [location_id, debouncedSearch, applicabilityView]);

  useEffect(() => { load(); }, [load]);

  const toggleApplicability = useCallback(async (sku, nextValue) => {
    // Phase 9.6.1 UI fix — optimistic update so the switch flips
    // instantly. Previously we reloaded from the server after PATCH
    // with `filter=applicable`, which either dropped the toggled row
    // (when nextValue=false) or produced a race where the user saw
    // the switch stay ON. Now we:
    //   1. Snapshot the row so we can revert on failure.
    //   2. Mutate local state immediately: is_applicable + source.
    //   3. Fire PATCH. On failure, revert + toast.
    //   4. NO reload. User's filter dropdown drives when data
    //      re-fetches — so the toggled row stays visible with the
    //      correct switch position until the operator switches views.
    setSavingSku(sku);
    let snapshotItems = null;
    setDetail((d) => {
      if (!d) return d;
      snapshotItems = d.items || [];
      return {
        ...d,
        items: (d.items || []).map((r) =>
          r.sku === sku
            ? {
                ...r,
                is_applicable: !!nextValue,
                applicability_source: "manual",
              }
            : r,
        ),
      };
    });
    try {
      await api.patch(
        `/fnb/locations/${encodeURIComponent(location_id)}/items/${encodeURIComponent(sku)}/applicability`,
        { is_applicable: !!nextValue },
      );
      toast.success(
        nextValue
          ? "Marked applicable. This won't be reset by future data uploads."
          : "Marked not applicable. This won't be reset by future data uploads.",
      );
    } catch (e) {
      // Revert the optimistic update.
      if (snapshotItems !== null) {
        setDetail((d) => d ? { ...d, items: snapshotItems } : d);
      }
      toast.error(detailFromError(e) || "Could not save. Try again.");
    } finally {
      setSavingSku(null);
    }
  }, [location_id]);

  const bulkToggle = useCallback(async (nextValue) => {
    if (!selectedSkus.size) {
      toast.error("Select at least one item first");
      return;
    }
    const selected = new Set(selectedSkus);
    setSavingBulk(true);
    // Optimistic bulk update.
    let snapshotItems = null;
    setDetail((d) => {
      if (!d) return d;
      snapshotItems = d.items || [];
      return {
        ...d,
        items: (d.items || []).map((r) =>
          selected.has(r.sku)
            ? {
                ...r,
                is_applicable: !!nextValue,
                applicability_source: "manual",
              }
            : r,
        ),
      };
    });
    try {
      const updates = Array.from(selected).map((sku) => ({
        item_sku: sku, is_applicable: !!nextValue,
      }));
      const res = await api.post(
        `/fnb/locations/${encodeURIComponent(location_id)}/items/applicability/bulk`,
        { updates },
      );
      toast.success(
        `${res.data?.upserted ?? 0} added · ${res.data?.modified ?? 0} updated. Persistent — future uploads won't touch these.`,
      );
      setSelectedSkus(new Set());
    } catch (e) {
      if (snapshotItems !== null) {
        setDetail((d) => d ? { ...d, items: snapshotItems } : d);
      }
      toast.error(detailFromError(e) || "Bulk update failed.");
    } finally {
      setSavingBulk(false);
    }
  }, [location_id, selectedSkus]);

  // Persist filters in URL for shareable deep-links
  useEffect(() => {
    const next = new URLSearchParams();
    if (statusFilter) next.set("status", statusFilter);
    if (category) next.set("category", category);
    if (debouncedSearch) next.set("search", debouncedSearch);
    setSearchParams(next, { replace: true });
  }, [statusFilter, category, debouncedSearch, setSearchParams]);

  const csvHref = useMemo(() => {
    const params = new URLSearchParams({
      scope: "location", location: location_id, format: "csv",
      item_scope: "daily",
    });
    if (statusFilter) params.set("status", statusFilter);
    if (category) params.set("category", category);
    if (debouncedSearch) params.set("search", debouncedSearch);
    return `${API}/fnb/analytics/reorder-status?${params}`;
  }, [location_id, statusFilter, category, debouncedSearch]);

  if (loading && !detail) {
    return <div className="text-sm text-muted-foreground" data-testid="loc-detail-loading">Loading…</div>;
  }
  if (error) {
    return (
      <Alert variant="destructive" data-testid="loc-detail-error">
        <AlertTitle>Couldn&apos;t load location</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }
  if (!detail) return null;

  const loc = detail.location;
  const stats = detail.stats;
  const items = detail.items || [];
  const pill = HEALTH_PILL[stats.health] || HEALTH_PILL.green;

  return (
    <div className="space-y-6" data-testid="fnb-location-detail">
      <header className="space-y-2">
        <nav
          className="text-xs text-muted-foreground flex items-center gap-1"
          data-testid="loc-breadcrumb"
        >
          <Link to="/tools/fnb-po" className="hover:text-foreground underline underline-offset-4">
            Dashboard
          </Link>
          <span>›</span>
          <span className="text-foreground/80">{loc.display_name}</span>
        </nav>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1
              className="text-2xl font-semibold tracking-tight"
              data-testid="loc-detail-heading"
            >
              {loc.display_name}
            </h1>
            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
              {loc.address && <div>{loc.address}</div>}
              <div>
                <span className="font-mono">{loc.location_id}</span>
                {" · "}
                <span>{loc.is_po_target ? "PO target" : "Non-PO-target"}</span>
                {loc.is_warehouse && " · Warehouse"}
              </div>
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${pill.cls}`}
            data-testid="loc-health-pill"
          >
            {pill.label} · pct_oos {(stats.pct_oos * 100).toFixed(1)}%
          </span>
        </div>
      </header>

      <Card data-testid="loc-stats-card">
        <CardContent className="pt-6 grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
          <Stat
            label="Total items"
            value={stats.items_total}
            testid="loc-stat-total"
            sub={
              stats.items_inactive_here_hidden > 0
                ? `+ ${stats.items_inactive_here_hidden} inactive here excluded`
                : null
            }
          />
          <Stat label="OK"              value={stats.items_ok}            testid="loc-stat-ok"    accent="text-emerald-700" />
          <Stat label="Need reorder"    value={stats.items_below_reorder} testid="loc-stat-below" accent="text-amber-800" />
          <Stat label="Out of stock"    value={stats.items_out_of_stock}  testid="loc-stat-oos"   accent="text-rose-700" />
          <Stat
            label="Unconfigured"
            value={stats.items_unconfigured || 0}
            testid="loc-stat-unconfigured"
            accent="text-slate-600"
            sub="Awaiting file rule"
          />
          <Stat label="% OOS"           value={`${(stats.pct_oos * 100).toFixed(1)}%`} testid="loc-stat-pctoos" />
        </CardContent>
      </Card>

      <Card data-testid="loc-items-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base">Items at {loc.display_name}</CardTitle>
              <CardDescription className="text-xs">
                Sales averages come from the last 7 / 14 / 30 days of POS data mapped via BOM.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={csvHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-foreground/70 hover:text-foreground underline underline-offset-4"
                data-testid="loc-csv-export"
              >
                Export CSV
              </a>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={applicabilityView}
              onValueChange={(v) => setApplicabilityView(v)}
            >
              <SelectTrigger className="w-[200px] text-xs" data-testid="loc-applicability-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="applicable">Applicable</SelectItem>
                <SelectItem value="not_applicable">Not applicable</SelectItem>
                <SelectItem value="auto_excluded">Auto-excluded</SelectItem>
                <SelectItem value="all">All items</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={statusFilter || "__all"}
              onValueChange={(v) => setStatusFilter(v === "__all" ? "" : v)}
            >
              <SelectTrigger className="w-[160px] text-xs" data-testid="loc-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All statuses</SelectItem>
                {["out_of_stock", "danger", "reorder", "unconfigured", "ok", "overstocked", "unknown"].map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s]?.label || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Search SKU or name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-[220px] text-xs"
              data-testid="loc-search"
            />
            {selectedSkus.size > 0 && (
              <div
                className="flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 px-2 py-1"
                data-testid="loc-bulk-bar"
              >
                <span className="text-[11px] font-medium text-emerald-800">
                  {selectedSkus.size} selected
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[11px]"
                  disabled={savingBulk}
                  onClick={() => bulkToggle(true)}
                  data-testid="loc-bulk-mark-applicable"
                >
                  Mark applicable
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[11px]"
                  disabled={savingBulk}
                  onClick={() => bulkToggle(false)}
                  data-testid="loc-bulk-mark-not-applicable"
                >
                  Mark not applicable
                </Button>
                <button
                  className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-4"
                  onClick={() => setSelectedSkus(new Set())}
                  data-testid="loc-bulk-clear"
                >
                  clear
                </button>
              </div>
            )}
          </div>
          {stats.items_inactive_here_hidden > 0 && !includeInactiveHere && (
            <p
              className="text-[11px] text-muted-foreground italic"
              data-testid="loc-inactive-here-hidden-note"
            >
              {stats.items_inactive_here_hidden} items are not sold at this outlet in the last 90 days — hidden by default.
            </p>
          )}

          <div className="border border-border/60 rounded-md overflow-x-auto">
            <Table data-testid="loc-items-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[11px] w-8">
                    <Checkbox
                      checked={items.length > 0 && selectedSkus.size === items.length}
                      onCheckedChange={(v) => {
                        if (v) setSelectedSkus(new Set(items.map((r) => r.sku)));
                        else setSelectedSkus(new Set());
                      }}
                      data-testid="loc-header-select-all"
                    />
                  </TableHead>
                  <TableHead className="text-[11px]">SKU</TableHead>
                  <TableHead className="text-[11px]">Name</TableHead>
                  <TableHead className="text-[11px]">Applicable</TableHead>
                  <TableHead className="text-[11px]">Category</TableHead>
                  <TableHead className="text-[11px]">UOM</TableHead>
                  <TableHead className="text-[11px] text-right">Qty avail</TableHead>
                  <TableHead className="text-[11px] text-right">Sold 7d</TableHead>
                  <TableHead className="text-[11px] text-right">Sold 30d</TableHead>
                  <TableHead className="text-[11px] text-right text-muted-foreground">Sold 90d</TableHead>
                  <TableHead className="text-[11px] text-right">Reorder</TableHead>
                  <TableHead className="text-[11px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center text-xs text-muted-foreground py-6">
                      No items match the current filters.
                    </TableCell>
                  </TableRow>
                )}
                {items.map((r) => {
                  const inactive = r.is_menu_active === false;
                  const inactiveHere = r.status === "inactive_here";
                  const slowMoving = r.status === "slow_moving";
                  const notApplicable = r.is_applicable === false;
                  const rowCls = notApplicable
                    ? "opacity-60 bg-slate-50/50"
                    : inactiveHere
                      ? "opacity-60 bg-slate-50/60"
                      : slowMoving
                        ? "bg-indigo-50/40"
                        : inactive ? "opacity-55 bg-muted/20" : "";
                  const isSelected = selectedSkus.has(r.sku);
                  const manualBadge = r.applicability_source === "manual" ? (
                    <Badge
                      variant="outline"
                      className="text-[9px] py-0 px-1.5 border-emerald-500/50 text-emerald-800"
                      data-testid={`loc-manual-badge-${r.sku}`}
                    >
                      Manual
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="text-[9px] py-0 px-1.5 border-slate-300 text-slate-500"
                      data-testid={`loc-auto-badge-${r.sku}`}
                    >
                      Auto
                    </Badge>
                  );
                  return (
                    <TableRow
                      key={r.sku}
                      className={rowCls}
                      data-testid={`loc-item-row-${r.sku}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(v) => {
                            const next = new Set(selectedSkus);
                            if (v) next.add(r.sku); else next.delete(r.sku);
                            setSelectedSkus(next);
                          }}
                          data-testid={`loc-select-${r.sku}`}
                        />
                      </TableCell>
                      <TableCell className="text-[11px] font-mono">
                        <Link
                          to={`/tools/fnb-po/items/${encodeURIComponent(r.sku)}`}
                          className="hover:underline underline-offset-4"
                        >
                          {r.sku}
                        </Link>
                      </TableCell>
                      <TableCell className="text-[12px] font-medium max-w-[240px]">
                        <div className="flex items-center gap-2">
                          <span className="truncate" title={r.name}>{r.name}</span>
                          {inactive && (
                            <Badge variant="outline" className="text-[9px] py-0 px-1.5 border-slate-400 text-slate-600">
                              Inactive
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={r.is_applicable}
                            onClick={() => toggleApplicability(r.sku, !r.is_applicable)}
                            disabled={savingSku === r.sku}
                            data-testid={`loc-applicability-toggle-${r.sku}`}
                            className={`h-4 w-7 rounded-full transition-colors ${
                              r.is_applicable
                                ? "bg-emerald-600"
                                : "bg-slate-300"
                            } relative disabled:opacity-50`}
                          >
                            <span
                              className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${
                                r.is_applicable ? "left-3.5" : "left-0.5"
                              }`}
                            />
                          </button>
                          {manualBadge}
                        </div>
                      </TableCell>
                      <TableCell className="text-[11px] text-muted-foreground">{r.category || "—"}</TableCell>
                      <TableCell className="text-[11px] text-muted-foreground">{r.uom || "—"}</TableCell>
                      <TableCell className="text-[12px] text-right font-mono">{num(r.qty_available, 1)}</TableCell>
                      <TableCell className="text-[11px] text-right font-mono">{intOrDash(r.qty_sold_7d)}</TableCell>
                      <TableCell className="text-[11px] text-right font-mono">{intOrDash(r.qty_sold_30d)}</TableCell>
                      <TableCell className="text-[11px] text-right font-mono text-muted-foreground">{intOrDash(r.qty_sold_90d)}</TableCell>
                      <TableCell className="text-[11px] text-right font-mono">{num(r.reorder_level)}</TableCell>
                      <TableCell><StatusPill status={r.status} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="text-[11px] text-muted-foreground">
            Showing {items.length} items · view: <b>{applicabilityView}</b>
            {stats.items_returned !== undefined && stats.items_returned !== items.length && (
              <> · returned {stats.items_returned}</>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, testid, accent, sub }) {
  return (
    <div className="space-y-0.5" data-testid={testid}>
      <div className={`text-2xl font-semibold ${accent || ""}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      {sub && (
        <div className="text-[10px] text-muted-foreground italic" data-testid={`${testid}-sub`}>
          {sub}
        </div>
      )}
    </div>
  );
}

// Phase 9.5.2 — qty_sold_* columns render as integers. Null = no BOM
// mapping (unknown), 0 = mapped but genuinely zero sold.
function intOrDash(v) {
  if (v === null || v === undefined) return "—";
  return String(Math.round(v));
}
